import React, { FC, ReactElement, useMemo } from 'react'
import FilterListIcon from '@material-ui/icons/FilterList'
import CategoryIcon from '@material-ui/icons/Category'
import { IsVarEmpty, isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import type { SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'
import { SelectFilter } from '../../Molecules/AdminFilters/SelectFilter'
import { SelectFilterMulti } from '../../Molecules/AdminFilters/SelectFilterMulti'
import { AdminFiltersOutlinedDateTime } from '../../Molecules/AdminFilters/AdminFiltersOutlinedDateTime'
import type { AdminFiltersSlotRenderArgs } from '../../Molecules/AdminFilters/AdminFilters'
import { TextFilterNames } from '../../Molecules/AdminFilters/AdminFiltersTextFilterNames'

export type ConfigChangeLogListAdditionalFiltersProps = AdminFiltersSlotRenderArgs & {
    /** Label for the audit trail upper-bound date-time picker. */
    readonly changeLogBeforeLabel: string
    readonly changeLogActionValues: readonly string[]
    readonly changeLogTypeValues: readonly string[]
    readonly changeLogActionOptionLabel: (action: string) => string
    readonly changeLogTypeOptionLabel: (entityType: string) => string
    readonly changeLogActionsLabel: string
    readonly changeLogTypesLabel: string
    readonly changeLogSuppressNullsLabel: string
    readonly changeLogSuppressNullsNoLabel: string
    readonly changeLogSuppressNullsYesLabel: string
    /** When more than one action or entity type is selected, label for the single collapsed chip (receives count). */
    readonly formatChangeLogMultiSelectSelectionLabel: (selectedCount: number) => string
}

/**
 * Audit trail filters rendered in the {@link AdminFilters} {@code additionalFilters} slot: before date-time,
 * action multi-select, entity / table type multi-select, and suppress-null-values single select.
 *
 * <p>Controls use the same {@link SelectFilter} / {@link SelectFilterMulti} pattern as other admin lists so labels
 * and control sizing match the shared toolbar.</p>
 *
 * <p>Username search uses the shared {@link AdminFilters} search field (see {@code searchLabel} on the list page).</p>
 *
 * @param props - Slot args from {@link AdminFilters} plus value lists, labels, option mappers, and collapsed
 * multi-select label formatter.
 * @returns Fragment of filter controls aligned with other admin list additional filters.
 */
export const ConfigChangeLogListAdditionalFilters: FC<ConfigChangeLogListAdditionalFiltersProps> = ({
    isLoading,
    filterValues,
    onTextFilterChange,
    onChangeLogActionsChange,
    onChangeLogTypesChange,
    onChangeLogSuppressNullsChange,
    changeLogBeforeLabel,
    changeLogActionValues,
    changeLogTypeValues,
    changeLogActionOptionLabel,
    changeLogTypeOptionLabel,
    changeLogActionsLabel,
    changeLogTypesLabel,
    changeLogSuppressNullsLabel,
    changeLogSuppressNullsNoLabel,
    changeLogSuppressNullsYesLabel,
    formatChangeLogMultiSelectSelectionLabel
}: ConfigChangeLogListAdditionalFiltersProps): ReactElement => {
    const actionOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] =>
            changeLogActionValues
                .filter((action: string): boolean => isStringNotEmpty(action))
                .map(
                    (action: string): SelectFilterOption => ({
                        value: action,
                        label: changeLogActionOptionLabel(action)
                    })
                ),
        [changeLogActionOptionLabel, changeLogActionValues]
    )

    const typeOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] =>
            changeLogTypeValues.map(
                (entityType: string): SelectFilterOption => ({
                    value: entityType,
                    label: changeLogTypeOptionLabel(entityType)
                })
            ),
        [changeLogTypeOptionLabel, changeLogTypeValues]
    )

    const suppressNullOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: 'no', label: changeLogSuppressNullsNoLabel },
            { value: 'yes', label: changeLogSuppressNullsYesLabel }
        ],
        [changeLogSuppressNullsNoLabel, changeLogSuppressNullsYesLabel]
    )

    const selectedActionOptions: SelectFilterOption[] = useMemo((): SelectFilterOption[] => {
        const selected: readonly string[] = filterValues.changeLogActions ?? []

        return actionOptions.filter((opt: SelectFilterOption): boolean =>
            selected.some((v: string): boolean => String(opt.value) === v)
        )
    }, [actionOptions, filterValues.changeLogActions])

    const selectedTypeOptions: SelectFilterOption[] = useMemo((): SelectFilterOption[] => {
        const selected: readonly string[] = filterValues.changeLogTypes ?? []

        return typeOptions.filter((opt: SelectFilterOption): boolean =>
            selected.some((v: string): boolean => String(opt.value) === v)
        )
    }, [filterValues.changeLogTypes, typeOptions])

    const suppressNullValue: SelectFilterOption = useMemo((): SelectFilterOption => {
        if (filterValues.changeLogSuppressNulls === true) return suppressNullOptions[1]

        return suppressNullOptions[0]
    }, [filterValues.changeLogSuppressNulls, suppressNullOptions])

    if (
        IsVarEmpty(onTextFilterChange) ||
        IsVarEmpty(onChangeLogActionsChange) ||
        IsVarEmpty(onChangeLogTypesChange) ||
        IsVarEmpty(onChangeLogSuppressNullsChange)
    ) {
        return <></>
    }

    return (
        <>
            <div>
                <AdminFiltersOutlinedDateTime
                    label={changeLogBeforeLabel}
                    disabled={isLoading}
                    value={filterValues.changeLogBefore ?? EMPTY_STRING}
                    onChange={(next: string): void => onTextFilterChange(TextFilterNames.CHANGE_LOG_BEFORE, next)}
                />
            </div>
            <div data-testid='changelog-filter-actions'>
                <SelectFilterMulti
                    disabled={isLoading}
                    label={changeLogActionsLabel}
                    icon={<FilterListIcon />}
                    value={selectedActionOptions}
                    options={actionOptions}
                    formatMultipleSelectionLabel={formatChangeLogMultiSelectSelectionLabel}
                    onChange={(next: SelectFilterOption[]): void => {
                        onChangeLogActionsChange(next.map((o: SelectFilterOption): string => String(o.value)))
                    }}
                />
            </div>
            <div data-testid='changelog-filter-entity-types'>
                <SelectFilterMulti
                    disabled={isLoading}
                    label={changeLogTypesLabel}
                    icon={<CategoryIcon />}
                    value={selectedTypeOptions}
                    options={typeOptions}
                    formatMultipleSelectionLabel={formatChangeLogMultiSelectSelectionLabel}
                    onChange={(next: SelectFilterOption[]): void => {
                        onChangeLogTypesChange(next.map((o: SelectFilterOption): string => String(o.value)))
                    }}
                />
            </div>
            <div data-testid='changelog-filter-suppress-nulls'>
                <SelectFilter
                    disabled={isLoading}
                    label={changeLogSuppressNullsLabel}
                    icon={<FilterListIcon />}
                    value={suppressNullValue}
                    options={suppressNullOptions}
                    onChange={(opt: Nullable<SelectFilterOption>): void => {
                        onChangeLogSuppressNullsChange(opt?.value === 'yes')
                    }}
                    defaultValue='no'
                    allowClearable={false}
                />
            </div>
        </>
    )
}
